import React, { useCallback, useEffect, useState } from 'react';
import { Shield, ChevronRight, Users, KeyRound, Share2 } from 'lucide-react';
import '../../components/v2/adminManagement/AdminManagement.css';
import { useSiteContext } from '../../SiteContext';
import {
  AdminEmployeeListItem, AdminRoleWithUsage, EmployeeLite,
  getAdminEmployees, getAdminEmployeeRoles, getEmployeesForLookup, getApiErrorMessage,
} from '../../api/adminManagement';
import { TabBar, TabDef } from '../../components/v2/adminManagement/AdminManagementKit';
import AdminEmployeesTab from '../../components/v2/adminManagement/AdminEmployeesTab';
import RolesTab from '../../components/v2/adminManagement/RolesTab';
import DelegationsTab from '../../components/v2/adminManagement/DelegationsTab';

type TabKey = 'employees' | 'roles' | 'delegations';
const TAB_STORAGE_KEY = 'am_tab';

/**
 * Admin Management (Approval Access WP9) — super-admin-only configuration page.
 * Page frame + reusable shared-component kit, with three tabs: Admin Employees
 * (WP9a), Admin Roles (WP9b), and Delegations (WP9c), all wired to the
 * WP3/WP6 Approval Access endpoints.
 *
 * The route is guarded super-admin-only in App.tsx (deep-link-safe redirect to Home),
 * and the nav item renders only for super admins (navConfig visible predicate). No
 * dependence on the global week pill — this is week-independent configuration.
 */
const AdminManagementPage: React.FC = () => {
  const { userID, userRole } = useSiteContext();

  const [tab, setTab] = useState<TabKey>(() => {
    try {
      const saved = localStorage.getItem(TAB_STORAGE_KEY);
      if (saved === 'employees' || saved === 'roles' || saved === 'delegations') return saved;
    } catch { /* ignore */ }
    return 'employees';
  });

  const [admins, setAdmins] = useState<AdminEmployeeListItem[]>([]);
  const [roles, setRoles] = useState<AdminRoleWithUsage[]>([]);
  const [employees, setEmployees] = useState<EmployeeLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Deep-link filter for Tab C (set when a Tab A delegation chip is clicked).
  const [delegateFilter, setDelegateFilter] = useState<{ adminEmployeeID: number; name: string } | null>(null);

  const [toast, setToast] = useState<{ msg: string; err: boolean } | null>(null);
  const showToast = useCallback((msg: string, err = false) => {
    setToast({ msg, err });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const changeTab = (k: TabKey) => {
    setTab(k);
    try { localStorage.setItem(TAB_STORAGE_KEY, k); } catch { /* ignore */ }
  };

  // Reload just the admin roster (after a mutation).
  const reloadAdmins = useCallback(async () => {
    try {
      const list = await getAdminEmployees('all');
      setAdmins(list);
    } catch (err) {
      showToast(getApiErrorMessage(err, 'Failed to reload admins.'), true);
    }
  }, [showToast]);

  // Reload the role list after a role mutation (also refreshes the tab-bar roles badge,
  // which is derived from `roles.length`).
  const reloadRoles = useCallback(async () => {
    try {
      const list = await getAdminEmployeeRoles();
      setRoles(list);
    } catch (err) {
      showToast(getApiErrorMessage(err, 'Failed to reload roles.'), true);
    }
  }, [showToast]);

  // Initial load: roles + employees + all admins in parallel.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [adminList, roleList, empList] = await Promise.all([
          getAdminEmployees('all'),
          getAdminEmployeeRoles(),
          getEmployeesForLookup(),
        ]);
        if (cancelled) return;
        setAdmins(adminList);
        setRoles(roleList);
        setEmployees(empList);
      } catch (err) {
        if (!cancelled) setLoadError(getApiErrorMessage(err, 'Failed to load Admin Management data.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  // Deep-link from a roster delegation chip — switch to Tab C filtered to this admin.
  const handleDelegationChip = (admin: AdminEmployeeListItem) => {
    setDelegateFilter({ adminEmployeeID: admin.adminEmployeeID, name: `${admin.name.first} ${admin.name.last}`.trim() });
    changeTab('delegations');
  };

  const tabs: TabDef<TabKey>[] = [
    { key: 'employees', label: 'Admin Employees', icon: Users, count: admins.length },
    { key: 'roles', label: 'Admin Roles', icon: KeyRound, count: roles.length },
    { key: 'delegations', label: 'Delegations', icon: Share2 },
  ];

  // Defensive gate (App.tsx already redirects non-super-admins).
  if (!userRole?.admin) {
    return (
      <div className="admin-mgmt">
        <div className="empty" style={{ marginTop: 40 }}>
          <div className="et">Not authorized</div>
          <p>Admin Management is available to super admins only.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-mgmt">
      {/* Breadcrumb */}
      <div className="crumbs">
        <span>People</span>
        <ChevronRight />
        <span className="here">Admin Management</span>
      </div>

      {/* Header */}
      <div className="app-head">
        <div className="logo"><Shield /></div>
        <div>
          <h1>Admin Management</h1>
          <div className="sub">
            Admin users, roles &amp; permissions, and approver delegations. Configure who can log into
            the admin app, what they can do, and whose employees they can act on.
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <TabBar tabs={tabs} active={tab} onChange={changeTab} />

      {/* Panes */}
      {loading ? (
        <div className="load-note"><span className="am-spin" /> Loading Admin Management…</div>
      ) : loadError ? (
        <div className="empty">
          <div className="et">Couldn't load Admin Management</div>
          <p>{loadError}</p>
        </div>
      ) : tab === 'employees' ? (
        <AdminEmployeesTab
          admins={admins}
          roles={roles}
          employees={employees}
          currentAdminEmployeeID={userID ?? null}
          onReload={reloadAdmins}
          onDelegationChip={handleDelegationChip}
          showToast={showToast}
        />
      ) : tab === 'roles' ? (
        <RolesTab roles={roles} onRolesChanged={reloadRoles} showToast={showToast} />
      ) : (
        <DelegationsTab
          admins={admins}
          employees={employees}
          deepLinkDelegate={delegateFilter}
          onClearDeepLink={() => setDelegateFilter(null)}
          onMutated={reloadAdmins}
          showToast={showToast}
        />
      )}

      {toast && <div className={`am-toast${toast.err ? ' err' : ''}`}>{toast.msg}</div>}
    </div>
  );
};

export default AdminManagementPage;
